/**
 * Web Push subscription utilities.
 *
 * Public API
 * ──────────
 *   • isSupported()             — env supports push?
 *   • currentPermission()       — "granted" | "denied" | "default" | "unsupported"
 *   • subscribe(getAuthHeaders) — request permission + register with backend
 *   • unsubscribe(getAuthHeaders) — drop from browser + backend
 *
 * Notes
 * ─────
 *   • Requires a registered Service Worker (we register from PwaInstallPrompt).
 *   • iOS only supports Web Push when the PWA is *installed* (added to Home
 *     Screen). We surface that hint in the UI when applicable.
 */
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const urlBase64ToUint8Array = (b64String) => {
  const padding = '='.repeat((4 - b64String.length % 4) % 4);
  const base64 = (b64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

export const isSupported = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export const currentPermission = () => {
  if (!isSupported()) return 'unsupported';
  return Notification.permission;
};

const getReadyRegistration = async () => {
  if (!('serviceWorker' in navigator)) throw new Error('no SW');
  const reg = await navigator.serviceWorker.ready;
  return reg;
};

export const subscribe = async (getAuthHeaders) => {
  if (!isSupported()) throw new Error('push-unsupported');

  // Permission
  let perm = Notification.permission;
  if (perm === 'default') perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    const err = new Error('permission-denied');
    err.code = perm;
    throw err;
  }

  // Public key from backend (single source of truth)
  let pubKey = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';
  if (!pubKey) {
    const r = await axios.get(`${API_URL}/push/public-key`);
    pubKey = r.data?.public_key || '';
  }
  if (!pubKey) throw new Error('no-vapid-key');

  const reg = await getReadyRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(pubKey),
    });
  }
  const subJson = sub.toJSON();
  await axios.post(`${API_URL}/push/subscribe`, {
    endpoint: subJson.endpoint,
    keys: subJson.keys,
    user_agent: navigator.userAgent,
  }, { headers: getAuthHeaders() });
  return subJson;
};

export const unsubscribe = async (getAuthHeaders) => {
  if (!isSupported()) return false;
  const reg = await getReadyRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;
  try {
    await axios.post(`${API_URL}/push/unsubscribe`, { endpoint: sub.endpoint },
      { headers: getAuthHeaders() });
  } catch (e) {
    // Best-effort: even if the server-side delete fails, we still want to
    // remove the local subscription so the browser stops receiving pushes.
    console.warn('[push] unsubscribe backend call failed:', e?.message || e);
  }
  await sub.unsubscribe().catch(() => null);
  return true;
};

export const getActiveSubscription = async () => {
  if (!isSupported()) return null;
  const reg = await getReadyRegistration();
  return reg.pushManager.getSubscription();
};
